/**
 * Action Plan V2 — MS.ActionPlan (local dev: http://localhost:6381).
 * Request có `actionPlanV2Api: true`; màn v2 còn được route qua isActionPlanV2ScreenActive().
 */
import { InternalAxiosRequestConfig } from 'axios';
import request, { postWithMissingDataAlert } from '../base-service';
import { ApiResponse } from '@/types/base';
import type { DashboardRequest } from '@/types/action-plan/DashboardRequest';
import type { DuAnDetailView } from '@/types/action-plan/DuAnDetailView';
import type { ActionPlanApiRequestOptions } from '@/configs/actionPlanV2ApiOrigin';
import { DuAnCongViecChiTietView, DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import { EditCongViecsRequest } from '@/types/action-plan/ActionPlan/EditCongViecsRequest';
import type { ExportCongViecRequest } from '@/types/action-plan/ExportCongViecRequest';
import type { ImportCongViecRequest } from '@/types/requestDTO';
import type {
  LoadTongQuanCongViecData,
  LoadTongQuanCongViecRequest,
} from '@/types/action-plan/LoadTongQuanCongViec';
import type {
  WorkspaceChildrenRequest,
  WorkspaceChildrenResponse,
  WorkspaceKanbanCardsRequest,
  WorkspaceKanbanCardsResponse,
  WorkspaceNodesRequest,
  WorkspaceNodesResponse,
  WorkspaceRootsRequest,
  WorkspaceRootsResponse,
  WorkspaceVersionsRequest,
  WorkspaceSaveRequest,
  WorkspaceSaveResponse,
  TiTrongPreviewRequest,
  TiTrongPreviewResponse,
  WorkspaceVersionsResponse,
} from '@/types/action-plan-v2/WorkspaceTree';
import type { EmployeeHomeResponse, EmployeeHomeLandingResponse, EmployeeHomeSummaryResponse, ManagerHomeResponse } from '@/types/action-plan-v2/EmployeeHome';
import type {
  ColumnConfig,
  ColumnConfigDeleteRequest,
  ColumnConfigListRequest,
  ColumnConfigSaveRequest,
  ColumnConfigValue,
  ColumnConfigValuesRequest,
  ColumnConfigValuesUpsertRequest,
} from '@/types/action-plan-v2/ColumnConfig';
import type {
  SearchCongViecRequest,
  SearchCongViecResponse,
} from '@/types/action-plan-v2/SearchCongViec';
import type {
  KanbanBoardV2Request,
  KanbanBoardV2Response,
  KanbanBoardsV2Request,
  KanbanBoardsV2Response,
} from '@/types/action-plan-v2/KanbanBoard';
import { normalizeEditCongViecsRequestForApi } from '@/utils/dateOnlyForApi';

export type EmployeeHomeTrangThai = 'overdue' | 'today' | 'upcoming' | 'inprogress' | 'inProgress' | 'completed';

export type EmployeeHomeTab = 'tasks' | 'managed' | 'goals';


export type CreateWorkflowTaskRequest = {
  taskInstanceId: string;
  controlId: string;
  mucTieuId: string;
  departmentId?: string;
  year: number;
  nguoiPhuTrachId: string;
  ngayKetThuc: string;
  tenCongViec: string;
  moTa?: string;
  filesDinhKem?: string;
};

export type CreateWorkflowTaskResponse = {
  id: string;
  duAnId: string;
  mucTieuId: string;
};

export type AiCreateTaskRequest = {
  idChoice: string;
  year: number;
  phanHe?: string;
  mucTieuId: string;
  tenCongViec: string;
  moTa?: string;
  nguoiPhuTrachId: string;
  ngayBatDau: string;
  ngayKetThuc: string;
  parentId?: string;
  chiTieu?: number;
  idDoLuong?: string;
};

export type AiCreateTaskResponse = {
  id: string;
  duAnId: string;
  mucTieuId: string;
};

export type AiUpdateTaskRequest = {
  taskId: string;
  idChoice: string;
  year: number;
  phanHe?: string;
  tenCongViec?: string;
  moTa?: string;
  ngayBatDau?: string;
  ngayKetThuc?: string;
};

export type AiGetTaskRequest = {
  taskId: string;
  idChoice: string;
  year: number;
  phanHe?: string;
};

export type AiGetTaskResponse = {
  id: string;
  tenCongViec?: string;
  moTa?: string;
  ngayBatDau?: string;
  ngayKetThuc?: string;
  mucTieuId?: string;
  nguoiPhuTrachId?: string;
};

export type AiListCongViecRequest = {
  idChoice: string;
  year: number;
  phanHe?: string;
  page?: number;
  pageSize?: number;
  query?: string;
  intent?: string;
  /** Xem việc người khác / phòng ban (chỉ đọc) — backend kiểm tra quyền Action Plan. */
  nguoiPhuTrachId?: string;
  tenNhanVien?: string;
  nguoiTaoId?: string;
  tenNguoiTao?: string;
  boPhanId?: string;
  tenBoPhan?: string;
  vaiTro?: string;
  trangThai?: string;
  onlyDuAn?: boolean;
};

export type AiListCongViecResponse = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  intent?: string;
  query?: string;
  items: Array<{
    id: string;
    tenCongViec?: string;
    ngayKetThuc?: string;
    ngayBatDau?: string;
    hasChild?: boolean;
    level?: number;
    bucket?: string;
    tenNguoiPhuTrach?: string;
    tenNguoiTao?: string;
    isMine?: boolean;
    isDuAn?: boolean;
  }>;
  scope?: string;
  viewTenNhanVien?: string;
  viewTenNguoiTao?: string;
  viewTenBoPhan?: string;
  ambiguousMatches?: Array<{ id: string; ten: string; boPhan?: string; loai?: string }>;
  /** Có khi gọi `ai/status-summary`. */
  tongViec?: number;
  thongKeTrangThai?: Record<string, number>;
};

export type AiScopeTreeRequest = {
  idChoice: string;
  year: number;
  phanHe?: string;
  boPhanId?: string;
  query?: string;
};

export type AiScopeTreeResponse = {
  /** all | tree | self */
  scope: string;
  phongBans: Array<{ id: string; ten: string; parentId?: string | null; level?: number }>;
  nhanViens: Array<{ id: string; ten: string; boPhanId?: string; tenBoPhan?: string; coTheGiao?: boolean }>;
  /** Quyền ghi theo cơ cấu: self | phong | all */
  quyenGhi?: string;
};

export type AiDeleteTaskRequest = {
  taskId: string;
  idChoice: string;
  year: number;
  phanHe?: string;
  confirm: boolean;
};

export type AiDeleteTaskResponse = {
  id: string;
  tenCongViec?: string;
  soViecCon: number;
  coTienDo: boolean;
  deleted: boolean;
  deletedIds?: string[];
};

export type CongViecTreePathRequest = {
  id: string;
  phanHe: string;
};

export type CongViecTreePathItem = {
  id: string;
  tenCongViec: string;
  parentID: string;
};

export type CongViecTreePathData = {
  id: string;
  treePath: string;
  treePathName: string;
  treePathCongViecList?: CongViecTreePathItem[];
};

export type TiTrongCongViecRequest = {
  id: string;
  year?: number;
  phanHe?: string;
};

export type TiTrongCongViecItem = {
  id: string;
  tenCongViec: string;
  tiTrong?: number | null;
  tiTrongCal?: number | null;
  tiTrongV2?: number | null;
  tiTrongCalV2?: number | null;
};

export type TiTrongCongViecData = {
  id: string;
  tenCongViec: string;
  parentID: string;
  duAnID: string;
  self: TiTrongCongViecItem;
  parent?: TiTrongCongViecItem | null;
  siblings: TiTrongCongViecItem[];
};

export type MucTieuParentsRequest = {
  IDMucTieu: string;
  PhanHe?: string;
};

export type MucTieuPathItem = {
  id: string;
  parentId?: string | null;
  ten?: string | null;
  level?: number;
};

export type MucTieuParentsResponse = {
  id: string;
  ten?: string | null;
  parentId?: string | null;
  parents?: MucTieuPathItem[];
  path?: MucTieuPathItem[];
};

export type MucTieuMindMapRequest = {
  IDChoice: string;
  Year: number;
  DateChoice?: string;
  PhanHe?: string;
  /** initial | roots | children | tasks */
  Mode?: 'initial' | 'roots' | 'children' | 'tasks' | string;
  ParentNodeId?: string;
  NodeId?: string;
  /** IDPhuTrach để lọc CV theo dashboard (Mode=tasks) */
  OwnerIds?: string[];
};

export type MucTieuMindMapDepartmentInfo = {
  id: string;
  name: string;
  type?: string;
  projectCount?: number;
  avatar?: string | null;
};

export type MucTieuMindMapNode = {
  id: string;
  name: string;
  parentId?: string | null;
  phuongDienId?: string | null;
  vitri?: number;
  mucTieuId?: string | null;
  /** ID phụ trách mục tiêu (BP/NV) */
  phuTrachId?: string | null;
  /** Tên phụ trách — badge trên mindmap */
  phuTrachName?: string | null;
  /** company | department | employee */
  phuTrachType?: string | null;
  /** Danh sách phụ trách mục tiêu (gom 1 node / mục tiêu) */
  phuTrachs?: MucTieuMindMapDepartmentInfo[];
  hasChildren?: boolean;
  hasActionPlans?: boolean;
  actionPlanCount?: number;
  /** Công việc root (action plan) gắn mục tiêu — lazy Mode=tasks */
  actionPlans?: DuAnCongViecTree[];
  /** Mục tiêu con */
  children?: MucTieuMindMapNode[];
};

export type MucTieuMindMapResponse = {
  current: MucTieuMindMapDepartmentInfo;
  /** Mode=initial: có MT dashboard (chưa load danh sách) */
  hasRoots?: boolean;
  roots: MucTieuMindMapNode[];
};

export type EmployeeHomeRequest = {
  ID: string;
  IDChoice?: string;
  Year: number;
  DateChoice?: string;
  Filter?: 'pt' | 'ht';
  PhanHe?: string;
  Tab?: EmployeeHomeTab;
  IDMucTieu?: string;
  TrangThai?: EmployeeHomeTrangThai;
  PageCurrent?: number;
  PageSize?: number;
  /** Tìm theo tên công việc (drawer / load-more). */
  Keyword?: string;
  /** Load đầy đủ landing: preview 3 khu + summary */
  IncludeLanding?: boolean;
  /** Landing kèm thống kê (mặc định true ở BE). Web gửi false và lấy thống kê qua `getEmployeeHomeSummary`. */
  IncludeSummary?: boolean;
};

/** overlap = giao kỳ BĐ/KT; created = NgayTao trong kỳ; started/ended = ngày BĐ/KT trong kỳ. */
export type ManagerHomeDateFilterMode = 'overlap' | 'created' | 'started' | 'ended';

export type ManagerHomeRequest = {
  ID: string;
  IDChoice?: string;
  Year: number;
  DateChoice?: string;
  /**
   * Cách lọc DateChoice. Manager Home + DateChoice: FE gửi `created`.
   * BE: có DateChoice mà thiếu mode → `created`; không DateChoice → không lọc ngày.
   */
  DateFilterMode?: ManagerHomeDateFilterMode;
  PhanHe?: string;
  /** Phòng: mặc định true. NV: BE bỏ qua (luôn đủ). */
  IncludeMtPhuTrach?: boolean;
  /** Phòng: mặc định true (load đủ rồi filter). NV: BE bỏ qua (luôn đủ). */
  IncludeMtKhongPhuTrach?: boolean;
  IDMucTieu?: string;
  PageCurrent?: number;
  PageSize?: number;
};

const v2Config = { authorization: true, actionPlanV2Api: true } as InternalAxiosRequestConfig;


// eslint-disable-next-line @typescript-eslint/no-explicit-any -- giữ any để caller APV2 không phải ép kiểu ApiResponse
const postApv2 = (
  url: string,
  body?: unknown,
  config: InternalAxiosRequestConfig = v2Config,
): Promise<any> => postWithMissingDataAlert(url, body, config);


export const legacyActionPlanConfig = {
  actionPlanApiTarget: 'legacy',
} satisfies ActionPlanApiRequestOptions;

export const actionPlanV2Api = {
  createWorkflowTask: (model: CreateWorkflowTaskRequest) => {
    return request.post<ApiResponse<CreateWorkflowTaskResponse>>(
      `/v1/actionplanv2/workflow-tasks`,
      model,
      v2Config
    );
  },
  /** POST: thống kê EmployeeHome (thẻ KPI, số trên tab, số theo trạng thái / MT) — tách khỏi landing. */
  getEmployeeHomeSummary: (model: EmployeeHomeRequest) => {
    return request.post<ApiResponse<EmployeeHomeSummaryResponse>>(
      `/v1/actionplanv2/employee-home-summary`,
      model,
      v2Config
    );
  },
  getEmployeeHome: (model: EmployeeHomeRequest) => {
    return request.post<ApiResponse<EmployeeHomeResponse | EmployeeHomeLandingResponse>>(
      `/v1/actionplanv2/employee-home`,
      {
        ...model,
        IncludeLanding: model.IncludeLanding,
      },
      v2Config
    );
  },
  getManagerHome: (model: ManagerHomeRequest) => {
    return request.post<ApiResponse<ManagerHomeResponse>>(
      `/v1/actionplanv2/manager-home`,
      model,
      v2Config
    );
  },
  editDuAn: (model: DuAnDetailView) => {
    return request.post<ApiResponse<boolean>>(`/v1/duan/editduan`, model, v2Config);
  },
  getBaoCaoNhanhViecTrongNgay: (model: DashboardRequest) => {
    return request.post<ApiResponse<unknown>>(
      // H43: route V2 — có cả việc gốc chưa có con (V1 bỏ qua).
      `/v1/actionplanv2/baocaonhanhcongviecduan`,
      model,
      v2Config
    );
  },
  loadMucTieu: (model: {
    idChoice: string;
    year: number;
    dateChoice?: string;
    search?: string;
    phanHe: string;
    filter?: 'pt' | 'ht';
    includeEmployeeHomeSummary?: boolean;
  }) => {
    return postApv2(
      `/v1/actionplanv2/loadmuctieu`,
      {
        IDChoice: model.idChoice,
        Year: model.year,
        DateChoice: model.dateChoice,
        Search: model.search,
        PhanHe: model.phanHe,
        Filter: model.filter,
        IncludeEmployeeHomeSummary: model.includeEmployeeHomeSummary,
      },
      { ...v2Config, silentError: true }
    );
 
  },
  getMucTieuMindMap: (model: {
    idChoice: string;
    year: number;
    dateChoice?: string;
    phanHe?: string;
    mode?: 'initial' | 'roots' | 'children' | 'tasks';
    parentNodeId?: string;
    nodeId?: string;
    ownerIds?: string[];
  }) => {
    return request.post<ApiResponse<MucTieuMindMapResponse>>(
      `/v1/actionplanv2/muctieu-mindmap`,
      {
        IDChoice: model.idChoice,
        Year: model.year,
        DateChoice: model.dateChoice ?? '',
        PhanHe: model.phanHe ?? 'bsc',
        Mode: model.mode ?? 'initial',
        ParentNodeId: model.parentNodeId,
        NodeId: model.nodeId,
        OwnerIds: model.ownerIds,
      } satisfies MucTieuMindMapRequest,
      v2Config
    );
  },
  listCongViecRoot: (model: {
    Selectbophan: string;
    Year: number;
    DateChoice?: string;
    /** Lọc theo mtctid; bỏ trống hoặc "0" = tất cả mục tiêu phụ trách */
    IDMucTieu?: string;
    /** pt | ht */
    Filter?: string;
    PhanHe?: string;
  }) => {
    return request.post<ApiResponse<DuAnCongViecTree[]>>(
      `/v1/actionplanv2/list-cong-viec-root`,
      {
        Selectbophan: model.Selectbophan,
        Year: model.Year,
        DateChoice: model.DateChoice,
        IDMucTieu: model.IDMucTieu,
        Filter: model.Filter ?? 'pt',
        PhanHe: model.PhanHe,
      },
      v2Config
    );
  },
  /** P15: xoá việc V2 (cả cây con + giá trị cột động; tỉ trọng anh em giữ nhập tay). */
  /** D-H10: xem trước xoá (chỉ đọc) — số việc trong cây và số việc con do người khác tạo. */
  xoaCongViecPreview: (model: { Ids: string[]; PhanHe: string; Year?: number }) => {
    return request.post<ApiResponse<{ tongSoViec: number; soViecCon: number; soViecConNguoiKhacTao: number; coTienDo: boolean; skipped: string[] }>>(
      `/v1/actionplanv2/xoa-cong-viec-preview`,
      model,
      v2Config
    );
  },
  xoaCongViec: (model: { Ids: string[]; PhanHe: string; Year?: number; Preview?: boolean }) => {
    return request.post<ApiResponse<{ deleted: string[]; changed: Array<{ id: string; tiTrongV2?: number | null; tiTrongCalV2?: number | null; hasChild: boolean; hinhThucTongHop?: number | null }> }>>(
      `/v1/actionplanv2/xoa-cong-viec`,
      model,
      v2Config
    );
  },
  editcongviecs: (model: EditCongViecsRequest) => {
    return request.post<ApiResponse<boolean>>(
      `/v1/actionplanv2/editcongviecs`,
      normalizeEditCongViecsRequestForApi(model),
      v2Config
    );
  },
  aiCreateTask: (model: AiCreateTaskRequest) =>
    request.post<ApiResponse<AiCreateTaskResponse>>(
      `/v1/actionplanv2/ai/create-task`,
      {
        IdChoice: model.idChoice,
        Year: model.year,
        PhanHe: model.phanHe ?? "bsc",
        MucTieuId: model.mucTieuId,
        TenCongViec: model.tenCongViec,
        MoTa: model.moTa,
        NguoiPhuTrachId: model.nguoiPhuTrachId,
        NgayBatDau: model.ngayBatDau,
        NgayKetThuc: model.ngayKetThuc,
        ParentId: model.parentId,
        ChiTieu: model.chiTieu,
        IdDoLuong: model.idDoLuong,
      },
      v2Config,
    ),
  aiUpdateTask: (model: AiUpdateTaskRequest) =>
    request.post<ApiResponse<{ id: string }>>(
      `/v1/actionplanv2/ai/update-task`,
      {
        TaskId: model.taskId,
        IdChoice: model.idChoice,
        Year: model.year,
        PhanHe: model.phanHe ?? "bsc",
        TenCongViec: model.tenCongViec,
        MoTa: model.moTa,
        NgayBatDau: model.ngayBatDau,
        NgayKetThuc: model.ngayKetThuc,
      },
      v2Config,
    ),
  aiGetTask: (model: AiGetTaskRequest) =>
    request.post<ApiResponse<AiGetTaskResponse>>(
      `/v1/actionplanv2/ai/get-task`,
      {
        TaskId: model.taskId,
        IdChoice: model.idChoice,
        Year: model.year,
        PhanHe: model.phanHe ?? "bsc",
      },
      v2Config,
    ),
  aiListCongViec: (model: AiListCongViecRequest) =>
    request.post<ApiResponse<AiListCongViecResponse>>(
      `/v1/actionplanv2/ai/list-cong-viec`,
      {
        IdChoice: model.idChoice,
        Year: model.year,
        PhanHe: model.phanHe ?? "bsc",
        Page: model.page ?? 1,
        PageSize: model.pageSize ?? 8,
        Query: model.query || undefined,
        Intent: model.intent || "browse",
        NguoiPhuTrachId: model.nguoiPhuTrachId || undefined,
        TenNhanVien: model.tenNhanVien || undefined,
        NguoiTaoId: model.nguoiTaoId || undefined,
        TenNguoiTao: model.tenNguoiTao || undefined,
        BoPhanId: model.boPhanId || undefined,
        TenBoPhan: model.tenBoPhan || undefined,
        VaiTro: model.vaiTro || undefined,
        TrangThai: model.trangThai || undefined,
        OnlyDuAn: model.onlyDuAn || undefined,
      },
      v2Config,
    ),
  aiStatusSummary: (model: AiListCongViecRequest) =>
    request.post<ApiResponse<AiListCongViecResponse>>(
      `/v1/actionplanv2/ai/status-summary`,
      {
        IdChoice: model.idChoice,
        Year: model.year,
        PhanHe: model.phanHe ?? "bsc",
        NguoiPhuTrachId: model.nguoiPhuTrachId || undefined,
        TenNhanVien: model.tenNhanVien || undefined,
        BoPhanId: model.boPhanId || undefined,
        TenBoPhan: model.tenBoPhan || undefined,
        VaiTro: model.vaiTro || undefined,
      },
      v2Config,
    ),
  aiScopeTree: (model: AiScopeTreeRequest) =>
    request.post<ApiResponse<AiScopeTreeResponse>>(
      `/v1/actionplanv2/ai/scope-tree`,
      {
        IdChoice: model.idChoice,
        Year: model.year,
        PhanHe: model.phanHe ?? "bsc",
        BoPhanId: model.boPhanId || undefined,
        Query: model.query || undefined,
      },
      v2Config,
    ),
  aiDeleteTask: (model: AiDeleteTaskRequest) =>
    request.post<ApiResponse<AiDeleteTaskResponse>>(
      `/v1/actionplanv2/ai/delete-task`,
      {
        TaskId: model.taskId,
        IdChoice: model.idChoice,
        Year: model.year,
        PhanHe: model.phanHe ?? "bsc",
        Confirm: model.confirm,
      },
      v2Config,
    ),
  chitiet: (model: DashboardRequest) => {
    return request.post<ApiResponse<DuAnCongViecChiTietView>>(
      `/v1/actionplanv2/chitiet`,
      model,
      v2Config
    );
  },
  getCongViecTreePath: (model: CongViecTreePathRequest) => {
    return request.post<ApiResponse<CongViecTreePathData>>(
      `/v1/actionplanv2/cong-viec-tree-path`,
      model,
      v2Config
    );
  },
  /** Tỉ trọng của công việc + cha + toàn bộ siblings (hover). */
  getTiTrongCongViec: (model: TiTrongCongViecRequest) => {
    return request.post<ApiResponse<TiTrongCongViecData>>(
      `/v1/actionplanv2/ti-trong-cong-viec`,
      {
        id: model.id,
        year: model.year,
        phanHe: model.phanHe ?? 'bsc',
      },
      v2Config
    );
  },
  /** Board Kanban của 1 việc gốc: cột + rule cha/con. Thao tác cột/thẻ dùng `setupactionplanApi`. */
  getKanbanBoard: (model: KanbanBoardV2Request) => {
    return request.post<ApiResponse<KanbanBoardV2Response>>(
      `/v1/actionplanv2/kanban-board`,
      { duAnId: model.duAnId, phanHe: model.phanHe ?? 'bsc' },
      v2Config
    );
  },
  /** Board nhiều việc gốc trong 1 request (tab Kanban). */
  getKanbanBoards: (model: KanbanBoardsV2Request) => {
    return request.post<ApiResponse<KanbanBoardsV2Response>>(
      `/v1/actionplanv2/kanban-boards`,
      { duAnIds: model.duAnIds, phanHe: model.phanHe ?? 'bsc' },
      v2Config
    );
  },
  /* ----- Workspace tải theo cấp (pha B, cờ `apv2.workspace.v2data`) ----- */
  /** Root hiển thị của các MT đang chọn + con của nhánh mở; lọc server-side. */
  workspaceRoots: (model: WorkspaceRootsRequest) => {
    return request.post<ApiResponse<WorkspaceRootsResponse>>(
      `/v1/actionplanv2/workspace/roots`,
      { ...model, phanHe: model.phanHe ?? 'bsc' },
      v2Config
    );
  },
  /** Con trực tiếp (hoặc cả nhánh `depth: 'all'`) của nhiều cha. */
  workspaceChildren: (model: WorkspaceChildrenRequest) => {
    return request.post<ApiResponse<WorkspaceChildrenResponse>>(
      `/v1/actionplanv2/workspace/children`,
      { ...model, phanHe: model.phanHe ?? 'bsc' },
      v2Config
    );
  },
  /** Đọc lại vài node theo id (sau báo cáo tiến độ / xoá). */
  workspaceNodes: (model: WorkspaceNodesRequest) => {
    return request.post<ApiResponse<WorkspaceNodesResponse>>(
      `/v1/actionplanv2/workspace/nodes`,
      { ...model, phanHe: model.phanHe ?? 'bsc' },
      v2Config
    );
  },
  /** Thẻ Kanban = việc của người xem ở mọi tầng (không suy từ cây đã tải). */
  workspaceKanbanCards: (model: WorkspaceKanbanCardsRequest) => {
    return request.post<ApiResponse<WorkspaceKanbanCardsResponse>>(
      `/v1/actionplanv2/workspace/kanban-cards`,
      { ...model, phanHe: model.phanHe ?? 'bsc' },
      v2Config
    );
  },
  /** Lưu theo thao tác (create / update / move / reorder / delete) — trả idMap, node đổi, xung đột. */
  workspaceSave: (model: WorkspaceSaveRequest) => {
    return request.post<ApiResponse<WorkspaceSaveResponse>>(
      `/v1/actionplanv2/workspace/save`,
      { ...model, phanHe: model.phanHe ?? 'bsc' },
      v2Config
    );
  },
  /** Xem trước chia tỉ trọng V2 nhóm anh em (không ghi DB). */
  workspaceTiTrongPreview: (model: TiTrongPreviewRequest) => {
    return request.post<ApiResponse<TiTrongPreviewResponse>>(
      `/v1/actionplanv2/workspace/ti-trong-preview`,
      { ...model, phanHe: model.phanHe ?? 'bsc' },
      v2Config
    );
  },
  /** MAX(NgayCapNhat) theo dự án — phát hiện người khác sửa. */
  workspaceVersions: (model: WorkspaceVersionsRequest) => {
    return request.post<ApiResponse<WorkspaceVersionsResponse>>(
      `/v1/actionplanv2/workspace/versions`,
      { ...model, phanHe: model.phanHe ?? 'bsc' },
      v2Config
    );
  },
  getMucTieuParents: (model: MucTieuParentsRequest) => {
    return request.post<ApiResponse<MucTieuParentsResponse>>(
      `/v1/actionplanv2/muctieu-parents`,
      {
        IDMucTieu: model.IDMucTieu,
        PhanHe: model.PhanHe ?? 'bsc',
      },
      v2Config
    );
  },

  /** Tìm CV theo tên / tên phụ trách — scope quyền giống legacy search. */
  searchCongViec: (model: SearchCongViecRequest) => {
    const page = Math.max(1, model.page ?? 1);
    const pageSize = Math.min(Math.max(1, model.pageSize ?? 20), 100);
    return request.post<ApiResponse<SearchCongViecResponse>>(
      `/v1/actionplanv2/search`,
      {
        phanHe: model.phanHe ?? 'bsc',
        idChoice: model.idChoice,
        year: model.year,
        keyword: model.keyword,
        page,
        pageSize,
        chiCongViecCon: model.chiCongViecCon ?? false,
      },
      v2Config
    );
  },

  /** Header cột động theo MTID */
  listColumnConfigs: (model: ColumnConfigListRequest) => {
    return request.post<ApiResponse<ColumnConfig[]>>(
      `/v1/actionplanv2/column-configs/list`,
      {
        mtid: model.mtid,
        phanHe: model.phanHe ?? 'bsc',
        includeInactive: model.includeInactive ?? false,
      },
      { ...v2Config, silentError: true }
    );
  },

  saveColumnConfig: (model: ColumnConfigSaveRequest) => {
    return request.post<ApiResponse<ColumnConfig>>(
      `/v1/actionplanv2/column-configs/save`,
      {
        id: model.id ?? null,
        tenColumn: model.tenColumn,
        loai: model.loai,
        mtid: model.mtid,
        viTri: model.viTri,
        isActive: model.isActive,
        details: model.details ?? null,
        phanHe: model.phanHe ?? 'bsc',
      },
      v2Config
    );
  },

  deleteColumnConfig: (model: ColumnConfigDeleteRequest) => {
    return request.post<ApiResponse<boolean>>(
      `/v1/actionplanv2/column-configs/delete`,
      {
        id: model.id,
        phanHe: model.phanHe ?? 'bsc',
      },
      v2Config
    );
  },

  /** Giá trị ô cột động theo list công việc */
  getColumnConfigValues: (model: ColumnConfigValuesRequest) => {
    return request.post<ApiResponse<ColumnConfigValue[]>>(
      `/v1/actionplanv2/column-configs/values`,
      {
        listCongViecID: model.listCongViecID,
        mtid: model.mtid,
        phanHe: model.phanHe ?? 'bsc',
      },
      { ...v2Config, silentError: true }
    );
  },

  /** Bố cục riêng của người đang đăng nhập (thứ tự / thu gọn cột Home, thứ tự / ẩn / độ rộng cột bảng) theo khoá. */
  getUserLayouts: (khoa: string[]) => {
    return request.post<ApiResponse<{ khoa: string; giaTri: string | null; ngayCapNhat?: string }[]>>(
      `/v1/actionplanv2/user-layouts/get`,
      { khoa, phanHe: 'bsc' },
      { ...v2Config, silentError: true }
    );
  },

  /** Ghi bố cục 1 khoá; `giaTri` null = xoá (về mặc định). */
  saveUserLayout: (khoa: string, giaTri: string | null) => {
    return request.post<ApiResponse<boolean>>(
      `/v1/actionplanv2/user-layouts/save`,
      { khoa, giaTri, phanHe: 'bsc' },
      { ...v2Config, silentError: true }
    );
  },

  upsertColumnConfigValues: (model: ColumnConfigValuesUpsertRequest) => {
    return request.post<ApiResponse<boolean>>(
      `/v1/actionplanv2/column-configs/values/upsert`,
      {
        items: model.items,
        phanHe: model.phanHe ?? 'bsc',
      },
      v2Config
    );
  },

  /** POST: export Excel công việc V2 (tỉ trọng effective V2). */
  export: (requestDTO: ExportCongViecRequest) => {
    return request.post(`/v1/actionplanv2/exportcongviec`, requestDTO || {}, {
      ...v2Config,
      responseType: 'blob',
      headers: { 'Content-Type': 'application/json' },
    } as InternalAxiosRequestConfig);
  },

  /** POST: template import — chỉ mục tiêu phụ trách theo IDChoice + Year. */
  exportTemplate: (requestDTO: ExportCongViecRequest) => {
    return request.post(`/v1/actionplanv2/exporttemplate`, requestDTO || {}, {
      ...v2Config,
      responseType: 'blob',
      headers: { 'Content-Type': 'application/json' },
    } as InternalAxiosRequestConfig);
  },

  /** POST: import công việc V2. [FromForm] ImportCongViecRequest. */
  import: (model: ImportCongViecRequest) => {
    const formData = new FormData();
    formData.append('PhanHe', model.PhanHe ?? '');
    formData.append('IDBoPhan', String(model.IDBoPhan ?? 0));
    formData.append('IDMucTieu', model.IDMucTieu ?? '');
    formData.append('IDDuAn', model.IDDuAn ?? '');
    formData.append('SelectBophan', model.SelectBophan ?? '');
    formData.append('Year', String(model.Year ?? new Date().getFullYear()));
    if (model.File) formData.append('File', model.File);
    return request.post<ApiResponse<boolean>>(`/v1/actionplanv2/importcongviec`, formData, {
      ...v2Config,
      headers: { 'Content-Type': 'multipart/form-data' },
    } as InternalAxiosRequestConfig);
  },

  loadTongQuanCongViecV2: (model: LoadTongQuanCongViecRequest) => {
    return request.post<ApiResponse<LoadTongQuanCongViecData>>(
      `/v1/actionplanv2/load-tong-quan-cong-viec`,
      {
        PhanHe: model.phanHe,
        IDChoice: model.idChoice,
        Year: model.year,
        DateChoice: model.dateChoice,
        HienThi: model.hienThi,
        ChiCongViecCon: model.chiCongViecCon,
        DuAnID: model.duAnID,
        DuAnIDs: model.duAnIDs,
        IDMucTieu: model.idMucTieu,
        IncludeMucTieu: model.includeMucTieu,
        MucTieuMacDinh: model.mucTieuMacDinh,
        Projects: model.projects,
        LazyExpandParentId: model.lazyExpandParentId,
        LazyExpandParentIds: model.lazyExpandParentIds,
        LazyExpandDepthAll: model.lazyExpandDepthAll,
        NguoiPhuTrachIDs: model.nguoiPhuTrachIDs,
        HideCompleted: model.hideCompleted,
      },
      v2Config
    );
  },
};

export { actionPlanApi } from '@/services/action-plan';
export { congviecduanApi as congviecduanApiV1 } from '@/services/action-plan/congviecduan';
/** Báo cáo: dùng chung `baocaocongviec` (không tách endpoint V2). */
export { baocaoApi as baoCaoV2Api } from '@/services/action-plan/baocao';
